import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from '@mui/material';
import { AccountTree, CleaningServices, Code, Construction } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PageContainer from '../../components/layout/PageContainer';
import PageField from '../../components/layout/PageField';
import Photo from '../../components/Photo/Photo';
import { approachSteps, hero, services, stackNote } from '../../content/site';
import { photos } from '../../content/photos';
import reactIcon from '../../assets/software-icons/react.svg';
import angularIcon from '../../assets/software-icons/angular.svg';
import typescriptIcon from '../../assets/software-icons/typescript.svg';
import dotNetIcon from '../../assets/software-icons/dot-net.svg';
import sqlIcon from '../../assets/software-icons/sqlserver2.svg';
import dockerIcon from '../../assets/software-icons/docker.svg';

const serviceIcons = [Code, Construction, AccountTree, CleaningServices];

const technologies = [
  { name: 'React', src: reactIcon },
  { name: 'Angular', src: angularIcon },
  { name: 'TypeScript', src: typescriptIcon },
  { name: '.NET', src: dotNetIcon },
  { name: 'SQL Server', src: sqlIcon },
  { name: 'Docker', src: dockerIcon },
];

const Home = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const band = dark ? '#0066b2' : '#0e2744';

  return (
    <Box>
      <PageField>
        <PageContainer>
          <Box sx={{ maxWidth: { md: '52%' }, py: { md: 2 } }}>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
              {hero.eyebrow}
            </Typography>
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.4rem', md: '3.5rem' }, maxWidth: 760, mt: 1 }}
            >
              {hero.title}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 640, fontSize: '1.2rem', lineHeight: 1.6, mt: 2 }}>
              {hero.lede}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 4 }}>
              <Button component={Link} to="/contact" variant="contained" size="large">
                Talk about a project
              </Button>
              <Button component={Link} to="/services" variant="outlined" size="large">
                View services
              </Button>
            </Box>
          </Box>
        </PageContainer>
        <Photo
          src={photos.circuit.src}
          alt={photos.circuit.alt}
          credit={photos.circuit.credit}
          fade="edge"
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            top: { md: 0 },
            right: { md: 0 },
            width: { xs: '100%', md: '48%' },
            height: { xs: 180, md: '100%' },
            opacity: dark ? 0.5 : 0.88,
          }}
        />
      </PageField>

      <PageContainer>
        <Typography variant="h2" sx={{ fontSize: { xs: '1.85rem', md: '2.25rem' }, mb: 1 }}>
          What we do
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 640, mb: 3 }}>
          Four services. We take the one that matches the system in front of you.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          {services.map((service, index) => {
            const Icon = serviceIcons[index];
            return (
              <Card
                key={service.id}
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: dark ? '#3d6ea3' : 'divider',
                  transition: 'border-color 160ms ease',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={`/services#${service.id}`}
                  sx={{ height: '100%', alignItems: 'stretch' }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Icon color="primary" />
                    <Typography variant="h3" sx={{ fontSize: '1.45rem', mt: 1.5, mb: 1 }}>
                      {service.title}
                    </Typography>
                    <Typography color="text.secondary">{service.summary}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      </PageContainer>

      <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
        <PageContainer>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.85rem', md: '2.25rem' }, mb: 3 }}>
            How we work
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
              gap: 3,
            }}
          >
            {approachSteps.map((step, index) => (
              <Box key={step.title}>
                <Typography color="primary" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontSize: '1.75rem', fontWeight: 600 }}>
                  {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography variant="h3" sx={{ fontSize: '1.25rem', mt: 1, mb: 1 }}>
                  {step.title}
                </Typography>
                <Typography color="text.secondary">{step.text}</Typography>
              </Box>
            ))}
          </Box>
          <Button component={Link} to="/approach" variant="text" sx={{ mt: 3, px: 0 }}>
            Read the approach
          </Button>
        </PageContainer>
      </Box>

      <PageContainer>
        <Typography variant="h2" sx={{ fontSize: { xs: '1.85rem', md: '2.25rem' }, mb: 1 }}>
          Built in your stack
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 680, mb: 3 }}>
          {stackNote}
        </Typography>
        <Box
          component="ul"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, listStyle: 'none', p: 0, m: 0 }}
        >
          {technologies.map((tech) => (
            <Box
              component="li"
              key={tech.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <img src={tech.src} alt="" width={28} height={28} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tech.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </PageContainer>

      <Box sx={{ bgcolor: band, color: '#ffffff', borderTop: dark ? '4px solid #dc052d' : 'none' }}>
        <PageContainer>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.85rem', md: '2.25rem' }, color: 'inherit', maxWidth: 640 }}>
            Tell us about the project.
          </Typography>
          <Typography sx={{ maxWidth: 560, mt: 1.5, color: 'rgba(255,255,255,0.8)' }}>
            A short description is enough. We will tell you whether we are the right firm for it.
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{ mt: 3, bgcolor: '#ffffff', color: '#0e2744', '&:hover': { bgcolor: '#e8eef5' } }}
          >
            Start a conversation
          </Button>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default Home;
